import React, { useMemo } from 'react';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import Snackbar from '../Snackbar/Snackbar';
import useSessionTimer from '../../hooks/useSessionTimer/useSessionTimer';
import { detectSessionUserType } from '../../utils/videoJoinToken';
import {
  formatClockDuration,
  getSessionReminderCopy,
  SESSION_DURATION_MINUTES,
} from '../../hooks/useSessionTimer/sessionTimerConfig';

const useStyles = makeStyles((theme: Theme) => ({
  indicator: {
    position: 'fixed',
    top: theme.spacing(1.25),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 11,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '6px 14px',
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
    pointerEvents: 'none',
    maxWidth: '92vw',
    [theme.breakpoints.down('sm')]: {
      top: `${theme.mobileTopBarHeight + 6}px`,
    },
  },
  finalWindow: {
    border: '1px solid #E46216',
    background: '#FFF8F0',
  },
  pastScheduled: {
    border: '1px solid #B0BEC5',
    background: '#F5F7F8',
  },
  primary: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    textAlign: 'center',
  },
  secondary: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    textAlign: 'center',
  },
  accent: {
    color: '#C45A00',
    fontWeight: 600,
  },
}));

interface SessionTimerProps {
  testMode?: boolean;
}

export default function SessionTimer({ testMode = false }: SessionTimerProps) {
  const classes = useStyles();
  const {
    elapsedSeconds,
    remainingSeconds,
    isConnected,
    isInFinalWindow,
    isPastScheduledDuration,
    shouldShowReminder,
    acknowledgeReminder,
  } = useSessionTimer(testMode);

  const userType = useMemo(() => detectSessionUserType(), []);
  const { headline, message } = getSessionReminderCopy(userType);

  if (!isConnected) {
    return null;
  }

  const elapsedLabel = formatClockDuration(elapsedSeconds);
  let primaryLine = `Time in session: ${elapsedLabel}`;
  let secondaryLine = `Scheduled consultation: ${SESSION_DURATION_MINUTES} minutes`;

  if (isPastScheduledDuration) {
    primaryLine = `Time in session: ${elapsedLabel}`;
    secondaryLine = 'Scheduled consultation time has passed. You may continue as needed.';
  } else if (isInFinalWindow) {
    primaryLine = `Time in session: ${elapsedLabel}`;
    secondaryLine = `${formatClockDuration(remainingSeconds)} remaining in scheduled consultation`;
  }

  const indicatorClass = [
    classes.indicator,
    isInFinalWindow && !isPastScheduledDuration ? classes.finalWindow : '',
    isPastScheduledDuration ? classes.pastScheduled : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={indicatorClass} role="status" aria-live="polite" aria-atomic="true">
        <Typography className={classes.primary}>{primaryLine}</Typography>
        <Typography
          className={`${classes.secondary} ${isInFinalWindow && !isPastScheduledDuration ? classes.accent : ''}`}
        >
          {secondaryLine}
        </Typography>
      </div>
      <Snackbar
        variant="warning"
        headline={headline}
        message={message}
        open={shouldShowReminder}
        handleClose={acknowledgeReminder}
      />
    </>
  );
}
